import {
  IconButton,
  InfoIcon,
  Text,
  TruncatedText,
  toast,
  type DataTableColumn,
} from "@carbon6/c6-frontend-design";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import {
  clearEpisodeInvalid,
  type EpisodeInvalidCategory,
  type InvalidEpisode,
} from "@/api/adminStorage";
import { isEpisodeExcluded } from "@/api/episodeReview";
import { openDatasetDetailWindow } from "@/app/datasetDetailWindow";
import { ResourceBrowserLayout, PaginationFooter } from "@/components/browse";
import { FilterResultCount } from "@/components/filter";
import { HatTextModal } from "@/components/modal";
import { BulkActionBar } from "@/components/table/BulkActionBar";
import bulkActionBarStyles from "@/components/table/BulkActionBar.module.css";
import { hatcheryTextModes } from "@/app/theme";
import { HatDataTable } from "@/components/table/HatDataTable";
import { useAdminTable } from "@/app/useAdminTable";
import { parsePageParam } from "@/utils";
import { DisabledReasonTooltip } from "@/components/tooltip";
import { adminOperationError, EXCLUDED_EPISODE_RECHECK_TEXT } from "@/app/adminOperationError";

import { InvalidEpisodeSource, type InvalidEpisodeRow } from "./StorageRecoveryTableSource";
import styles from "./AdminStoragePage.module.css";
import { tableStripError } from "@/components/table/tableStateMessages";

const CATEGORY_TEXT: Record<EpisodeInvalidCategory, string> = {
  file_defect: "파일 결함",
  content_defect: "내용 결함",
  unverified: "미확인",
};

// 재검사 확인 문구다. 재검사는 재진단 잡을 만들지 않는다. 결함 수용은 이 동작이 바꾸지 않는다.
const RECHECK_TEXT = {
  label: "에피소드를 재검사 대기로 되돌립니다.",
  caption:
    "검증 상태를 pending으로 바꾸고 사유를 지웁니다. 결함 수용과 코멘트는 유지됩니다. 재진단은 따로 요청해야 합니다. 재진단이 결함을 다시 찾으면 invalid로 돌아갑니다.",
};

export function InvalidEpisodesPanel() {
  const [params] = useSearchParams();
  const page = parsePageParam(params.get("page"));
  const state = params.get("state") === "accepted" ? "accepted" : "invalid";
  const source = useMemo(() => new InvalidEpisodeSource(state, page), [state, page]);
  const snapshot = useAdminTable(source);
  const { setPage } = snapshot;
  const currentSource = useRef(source);
  useEffect(() => {
    currentSource.current = source;
  }, [source]);
  const [pending, setPending] = useState<InvalidEpisode | null>(null);
  const [busy, setBusy] = useState(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const clear = async () => {
    if (pending === null || busy) return;
    setBusy(true);
    try {
      await clearEpisodeInvalid(pending.uuid);
      if (alive.current) toast.success("재검사 대기로 되돌렸습니다.");
    } catch (error) {
      if (alive.current) toast.error(adminOperationError(error));
    } finally {
      if (alive.current) {
        setPending(null);
        setBusy(false);
        await currentSource.current.refresh();
      }
    }
  };
  const columns: readonly DataTableColumn<InvalidEpisodeRow>[] = [
    {
      key: "episode",
      header: "에피소드 / 사유",
      width: "minmax(280px, 1fr)",
      render: (row) => (
        <div className={styles.details}>
          <TruncatedText>{row.episode.name}</TruncatedText>
          <TruncatedText>{row.episode.uuid}</TruncatedText>
          <TruncatedText>
            {row.episode.invalid_reason === null
              ? "사유 없음"
              : `${row.episode.invalid_reason} (${
                  row.episode.invalid_category === null
                    ? "분류 없음"
                    : CATEGORY_TEXT[row.episode.invalid_category]
                })`}
          </TruncatedText>
          {/* 채널 누락 에피소드는 업로드 때부터 제외 상태다. 재검사는 리뷰에서 복원한 뒤에만 된다. */}
          {isEpisodeExcluded(row.episode.status) ? (
            <span className={styles.excludedBadge}>제외됨</span>
          ) : null}
        </div>
      ),
    },
    {
      key: "accepted",
      header: "결함 수용",
      width: "100px",
      render: (row) => (row.accepted ? "수용함" : "-"),
    },
    {
      key: "datasets",
      header: "소속 Dataset",
      width: "280px",
      render: (row) => (
        <div className={styles.details}>
          {row.datasets.length === 0
            ? "소속 없음"
            : row.datasets.map((dataset) => (
                <IconButton
                  key={dataset.uuid}
                  className={styles.datasetLink}
                  text={<TruncatedText>{dataset.display_name}</TruncatedText>}
                  size="sm"
                  variant="ghost"
                  // 결함 수용은 리뷰 독이 바꾼다. 그래서 그 Dataset 의 에피소드 리뷰 탭을 창으로 연다.
                  onClick={() =>
                    openDatasetDetailWindow({
                      datasetUuid: dataset.uuid,
                      displayName: dataset.display_name,
                      initialTab: "episodeReview",
                    })
                  }
                />
              ))}
        </div>
      ),
    },
    {
      key: "action",
      header: "운영",
      width: "120px",
      // 재검사는 invalid 판정만 되돌린다. 수용했지만 invalid 가 아닌 행에는 되돌릴 판정이 없다.
      // 제외된 에피소드의 재검사는 서버가 409 `episode.excluded` 로 거절한다. 그래서 끄고 이유를 보인다.
      render: (row) =>
        row.episode.validation_status === "invalid" ? (
          <div className={styles.actions}>
            {isEpisodeExcluded(row.episode.status) ? (
              <DisabledReasonTooltip text={EXCLUDED_EPISODE_RECHECK_TEXT}>
                <IconButton text="재검사" size="sm" variant="surface" disabled />
              </DisabledReasonTooltip>
            ) : (
              <IconButton
                text="재검사"
                size="sm"
                variant="surface"
                disabled={busy}
                onClick={() => setPending(row.episode)}
              />
            )}
          </div>
        ) : (
          "-"
        ),
    },
  ];
  return (
    <div className={styles.sections}>
      <div className={styles.notice}>
        <span className={styles.noticeIcon} aria-hidden="true">
          <InfoIcon />
        </span>
        <Text variant={hatcheryTextModes["caption-normal"]} className={styles.noticeText}>
          자동 검증이 invalid로 처리한 에피소드입니다. 재검사는 pending으로 되돌리고 사유를
          지웁니다. 재진단을 요청하지 않으며, 결함 수용·코멘트·제외 여부·파일 내용은 바꾸지
          않습니다. 결함 수용은 소속 Dataset의 에피소드 리뷰에서 바꿉니다.
        </Text>
      </div>
      <ResourceBrowserLayout
        ariaLabel="Invalid 에피소드"
        summaryCount={snapshot.total}
        summaryLabel="invalid 에피소드"
        summaryInFilter
        error={tableStripError(snapshot)}
        onRetry={() => void source.refresh()}
        footer={
          <PaginationFooter
            page={page}
            size={snapshot.size}
            total={snapshot.total}
            isLoading={snapshot.loadState === "loading"}
            ariaLabel="Invalid 에피소드 페이지 이동"
            onPrevious={() => setPage(page - 1)}
            onNext={() => setPage(page + 1)}
          />
        }
      >
        <div className={bulkActionBarStyles.host}>
          <BulkActionBar
            ariaLabel="Invalid 에피소드 도구"
            source={source}
            idleContent={<FilterResultCount count={snapshot.total} unitLabel="invalid 에피소드" />}
            rightContent={
              <IconButton
                text="새로고침"
                size="sm"
                variant="surface"
                disabled={snapshot.loadState === "loading"}
                onClick={() => void source.refresh()}
              />
            }
          />
          <HatDataTable
            source={source}
            columns={columns}
            ariaLabel="Invalid 에피소드 테이블"
            emptyMessage="조건에 맞는 invalid 에피소드가 없습니다."
            minWidth="900px"
            scrollable
          />
        </div>
      </ResourceBrowserLayout>
      <HatTextModal
        open={pending !== null}
        label={RECHECK_TEXT.label}
        eyebrow={pending?.name}
        caption={RECHECK_TEXT.caption}
        busy={busy}
        onOkPress={() => void clear()}
        onCancelPress={() => setPending(null)}
      />
    </div>
  );
}
